import React, { useEffect, useState } from "react";
import {
    TextField,
    FormControl,
    Select,
    MenuItem,
    InputAdornment,
} from "@mui/material";
import { trakrefBlue, inputGrey, Grey3 } from "../../color_utils";
import PropTypes from "prop-types";
import { countryCode } from "../../Utils/PhoneCountryCode";
import { trakrefFontFamily, trakrefFontFamilySemiBold } from "../../utils";

const defaultStlyes = {
    "& label.Mui-focused": {
        color: trakrefBlue,
    },
    "& .MuiOutlinedInput-root": {
        "&.Mui-focused fieldset": {
            borderColor: trakrefBlue,
        },
    },
    "& .MuiFilledInput-root": {
        backgroundColor: inputGrey,
        "&.Mui-focused fieldset": {
            borderColor: trakrefBlue,
        },
    },
    "& .MuiFormLabel-root": { fontFamily: trakrefFontFamily },
    "&  .MuiFormLabel-root.MuiInputLabel-root.Mui-error": {
        color: "#D51A1A !important",
    },
    "& .MuiFormLabel-root.MuiInputLabel-shrink": {
        textTransform: "uppercase",
        fontFamily: trakrefFontFamilySemiBold,
    },
    "& .MuiFilledInput-root.Mui-error:after": {
        borderBottom: "2px solid #D51A1A !important",
    },
    "& .MuiFilledInput-root:after": {
        borderBottomColor: trakrefBlue,
    },
};

const validateValue = (value) => {
    if (!value && value.length <= 0) return false;

    const isValid = String(value)
        .toLowerCase()
        .match(/^(?:[ (]?[0-9][\s.-]?[)]?){10}$/);
    return !isValid;
};

const errorMessages =
    "Please enter a valid phone number. (For example : +1 (555) 555-1234)";

const formatValue = (val) => {
    if (!val)
        return "";

    if (val && val.length > 0 && val[0] === "+") {
        const i = val.indexOf(" ");
        return val.substring(val.length + 1, i).trimStart();
    }

    return val;
};

const InputMobileNumber = ({
    id,
    required,
    label,
    value,
    onChange,
    onKeyDown,
    onFocus,
    onBlur,
    error,
    fullWidth,
    variant,
    helperText,
    multiLine,
    type,
    disabled,
    rows,
    maxCharacterLength,
    sx,
    isValid,
    getCountryCode,
}) => {
    const [open, setOpen] = useState(false);
    const [currentValue, setCurrenttValue] = useState(value);
    const [currentCountryCode, setCurrentCountryCode] = useState("+1");
    const [currentError, setCurrentError] = useState(error);
    const [currentHelperText, setCurrentHelperText] = useState(errorMessages);

    useEffect(() => {
        if (value && value[0] === "+") {
            const existingCountryCode = value.split(" ")[0].trimStart();
            getCountryCode(existingCountryCode);
            setCurrentCountryCode(existingCountryCode);
        }
        else {
            getCountryCode(currentCountryCode);
        }

        if (helperText) {
            setCurrentHelperText(helperText);
        }

        setCurrenttValue(formatValue(value))
    }, []);

    useEffect(() => {
        const isValidValue = validateValue(formatValue(value));
        const errorExist = isValidValue || error;

        setCurrentError(errorExist);
        setCurrentHelperText(isValidValue ? errorMessages : helperText);
    }, [value, error]);

    useEffect(() => {
        isValid(currentError);
    }, [currentError]);

    const onOpenSelect = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleOnChange = (e) => {
        const { value } = e.target;

        const isValid = /^[0-9()\s.-]+$/.test(value);
        if (!isValid && value !== '') {
            return;
        }

        setCurrenttValue(value);
        onChange(e);
    }

    return (
        <div
            style={{
                position: "relative",
            }}
        >
            <TextField
                id={id}
                required={required}
                disabled={disabled}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <span
                                onClick={onOpenSelect}
                                style={{
                                    marginTop: "1px",
                                    cursor: "pointer",
                                    color: "#28313B",
                                }}
                            >
                                {currentCountryCode}
                            </span>
                        </InputAdornment>
                    ),
                    inputProps: {
                        sx: {
                            "&::placeholder": {
                                color: Grey3,
                                opacity: 1,
                                fontSize: '16px',
                            },
                            fontFamily: trakrefFontFamily
                        },
                        maxLength: maxCharacterLength,
                    }
                }}
                InputLabelProps={{
                    shrink: true,
                    sx: {
                        fontFamily: trakrefFontFamilySemiBold
                    }
                }}
                label={label}
                value={currentValue}
                defaultValue={currentValue}
                onChange={handleOnChange}
                onKeyDown={onKeyDown}
                onFocus={onFocus}
                onBlur={onBlur}
                error={currentError}
                rows={rows}
                fullWidth={fullWidth}
                variant={variant}
                helperText={currentError ? currentHelperText : ""}
                multiline={multiLine}
                type={type}
                sx={{ ...defaultStlyes, ...sx }}
            />
            <FormControl
                sx={{
                    minWidth: 100,
                    position: "absolute",
                    left: "0px",
                    clip: "rect(0 0 0 0)",
                    clipPath: "inset(50%)",
                }}
            >
                <Select
                    variant="outlined"
                    labelId="demo-controlled-open-select-label"
                    id="demo-controlled-open-select"
                    open={open}
                    onClose={handleClose}
                    onOpen={handleOpen}
                    onChange={(e) => {
                        setCurrentCountryCode(e.target.value);
                        getCountryCode(e.target.value);
                    }}
                >
                    {countryCode.map(({ name, dial_code }) => (
                        <MenuItem value={dial_code}>{`${name} ${dial_code}`}</MenuItem>
                    ))}
                </Select>
            </FormControl>
        </div>
    );
};

InputMobileNumber.propTypes = {
    required: PropTypes.bool,
    label: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onKeyDown: PropTypes.func,
    error: PropTypes.bool,
    fullWidth: PropTypes.bool,
    variant: PropTypes.string,
    helperText: PropTypes.string,
    multiLine: PropTypes.bool,
    numberInput: PropTypes.string,
    disabled: PropTypes.bool,
    maxCharacterLength: PropTypes.number,
    id: PropTypes.string,
    sx: PropTypes.object,
    isValid: PropTypes.func,
};

InputMobileNumber.defaultProps = {
    required: false,
    disabled: false,
    error: false,
    fullWidth: false,
    variant: "outlined",
    helperText: "This field is required",
    multiLine: false,
    numberInput: "",
    onFocus: () => { },
    onBlur: () => { },
    onKeyDown: () => { },
    maxCharacterLength: 50000,
    id: "",
    sx: {},
    isValid: () => { },
};

export default InputMobileNumber;
